import Grid from '@mui/material/Grid';
import { RHFAutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/RHFAutoCompleteVirtualize';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form';
import { Order_By } from '@openx/types/gqlTypes';
import type { ProvidersList } from '@openx/types/provider';
import { LalSegmentFields } from '@openx/types/segments';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { isUUID } from 'validator';
import { getLalSegmentLabels } from '../../../constants';
import { useSegmentContext } from '../../utils/context/SegmentContext';
import { SeedListsAdd } from '../components/SeedListsAdd/SeedListsAdd';
import { useHandleState } from '../components/SeedListsAdd/useHandleState';

export type SeedsProps = {
  isEdit?: boolean;
};

export function Seeds({ isEdit }: SeedsProps) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  const { useProvidersListFetch, useSegmentsInfinityScrollListFetch } = useSegmentContext();
  const observableAccountUid = useWatch({ name: LalSegmentFields.ACCOUNT_ID });
  const { handleRemoveAll } = useHandleState();

  const { providers, loading: providersLoading } = useProvidersListFetch(observableAccountUid);

  const observableProvider = useWatch({ name: LalSegmentFields.PROVIDER });

  const { segments, loadMore, hasMore, loadingMore, totalCount } = useSegmentsInfinityScrollListFetch({
    variables: {
      where: {
        provider: {
          id: { _eq: observableProvider?.id },
        },
        account: {
          id: { _eq: observableAccountUid },
        },
        _or: isUUID(searchTerm)
          ? [{ name: { _ilike: `%${searchTerm}%` } }, { id: { _eq: searchTerm } }]
          : [{ name: { _ilike: `%${searchTerm}%` } }],
      },
      limit: 15,
      order_by: [{ name: Order_By.Asc }],
    },
    skip: !observableProvider,
    fetchPolicy: 'network-only',
  });

  const labels = useMemo(() => getLalSegmentLabels(t), [t]);

  const {
    setValue,
    getValues,
    formState: { isSubmitting },
  } = useFormContext();

  useEffect(() => {
    if (providers.length === 1 && !observableProvider && !providersLoading) {
      setValue(LalSegmentFields.PROVIDER, providers[0]);
    }
  }, [providers, observableProvider, providersLoading, setValue]);

  const handleOnManageProviderChange = useCallback(
    (_, value) => {
      setValue(LalSegmentFields.PROVIDER, value);

      if (getValues(LalSegmentFields.SEEDS).length > 0) {
        setValue(LalSegmentFields.SEEDS, []);
      }

      handleRemoveAll();
    },
    [setValue, getValues, handleRemoveAll]
  );

  const isProviderDisabled = isSubmitting || isEdit || providers.length === 1;

  return (
    <FormSection
      divider
      title={t('Seed Segments')}
      rightColumnSize={RIGHT_COLUMN_WIDTH}
      description={t('Select seed segments to build your LAL segment.')}
    >
      <Grid container>
        <Grid size={12} sx={{ mb: 2 }}>
          <Grid container spacing={4}>
            <Grid size={12}>
              <RHFAutoCompleteVirtualize
                name={LalSegmentFields.PROVIDER}
                textFieldProps={{
                  label: labels[LalSegmentFields.PROVIDER],
                  fullWidth: true,
                  placeholder: `[ ${t('Select Provider')} ]`,
                }}
                disableClearable
                renderOptions={{
                  dataTest: 'provider-item',
                }}
                disabled={isProviderDisabled}
                getOptionLabel={option => option?.name || ''}
                options={providers}
                loading={providersLoading}
                value={observableProvider || null}
                onChange={handleOnManageProviderChange}
                isOptionEqualToValue={(option, value) => option?.id === (value as ProvidersList)?.id}
                data-test="seed-provider"
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid size={12} sx={{ pt: 2 }}>
          <SeedListsAdd
            segments={segments}
            loadMore={loadMore}
            hasMore={hasMore}
            loadingMore={loadingMore}
            allItemsCount={totalCount}
            onSearch={setSearchTerm}
          />
        </Grid>
      </Grid>
    </FormSection>
  );
}
